import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";

import shoelaceLight from "@shoelace-style/shoelace/dist/themes/light.styles.js";

const nodeOutlineTemplate = (node) =>
    html` <sl-tree-item
        ?expanded=${node?.content?.length &&
        !node?.content?.find((node) => node.type === "text")}
    >
        ${node.type === "text"
            ? `${node?.marks ? JSON.stringify(node.marks) : ""} ${node.text}`
            : html`
                  ${node.type} (${JSON.stringify(node.attrs)}):
                  <i
                      >${node?.content
                          ?.filter((node) => node.type === "text")
                          ?.map((textNode) => textNode.text)
                          ?.join("")
                          ?.replace(/(.{20})[^][^]+/, "$1…")}</i
                  >
                  ${repeat(node?.content ?? [], nodeOutlineTemplate)}
              `}
    </sl-tree-item>`;

@customElement("c-document-outline")
export class DocumentOutline extends LitElement {
    @property({ type: Object, attribute: false })
    outline = { type: "empty" };

    static styles = [
        shoelaceLight,
        css`
            :host,
            :root {
                --stekz-blue-950: color-mix(
                    in hsl,
                    var(--stekz-blue-darkest) 50%,
                    black
                );
                --stekz-blue-900: color-mix(
                    in hsl,
                    var(--stekz-blue) 0%,
                    var(--stekz-blue-darkest)
                );
                --stekz-blue-800: color-mix(
                    in hsl,
                    var(--stekz-blue) 40%,
                    var(--stekz-blue-darkest)
                );
                --stekz-blue-700: color-mix(
                    in hsl,
                    var(--stekz-blue) 60%,
                    var(--stekz-blue-darkest)
                );
                --stekz-blue-600: color-mix(
                    in hsl,
                    var(--stekz-blue) 90%,
                    var(--stekz-blue-darkest)
                );
                --stekz-blue-500: var(--stekz-blue);
                --stekz-blue-400: color-mix(
                    in hsl,
                    var(--stekz-blue) 50%,
                    var(--stekz-blue-lightest)
                );
                --stekz-blue-300: var(--stekz-blue-lightest);
                --stekz-blue-200: color-mix(
                    in hsl,
                    var(--stekz-blue-lightest) 66%,
                    white
                );
                --stekz-blue-100: color-mix(
                    in hsl,
                    var(--stekz-blue-lightest) 33%,
                    white
                );
                --stekz-blue-50: color-mix(
                    in hsl,
                    var(--stekz-blue-lightest) 10%,
                    white
                );
            }

            :host,
            :root {
                --sl-color-primary-950: var(--stekz-blue-950);
                --sl-color-primary-900: var(--stekz-blue-900);
                --sl-color-primary-800: var(--stekz-blue-800);
                --sl-color-primary-700: var(--stekz-blue-700);
                --sl-color-primary-600: var(--stekz-blue-600);
                --sl-color-primary-500: var(--stekz-blue-500);
                --sl-color-primary-400: var(--stekz-blue-400);
                --sl-color-primary-300: var(--stekz-blue-300);
                --sl-color-primary-200: var(--stekz-blue-200);
                --sl-color-primary-100: var(--stekz-blue-100);
                --sl-color-primary-50: var(--stekz-blue-50);
            }
        `,
        css`
            .document-outline {
                --indent-guide-width: 1px;
            }
        `,
    ];
    render() {
        return html` <sl-tree class="document-outline">
            ${nodeOutlineTemplate(this.outline)}
        </sl-tree>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "c-document-outline": DocumentOutline;
    }
}
